import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import { Button } from "src/@/components/ui/button";
import { Input } from 'src/@/components/ui/input';
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from 'src/@/components/ui/table';
import { Card } from 'src/@/components/ui/card';
import { Checkbox } from 'src/@/components/ui/checkbox';
import Scrollbar from 'src/components/Scrollbar';
import tarefasService from 'src/services/tarefasService';
import { actionCriarWorkflow } from 'src/actions/tarefas/actionTarefasConfig';
import useAuth from 'src/hooks/useAuth';
import { useToast } from 'src/@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "src/@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/@/components/ui/select";
import { getListaUsuariosByClienteId, getListaSetoresTransferencia } from 'src/redux/slices/user';
import PageContainerHeader from 'src/components/PageContainerHeader';
import TarefaDialog from './TarefaDialog';
import KanbanBoard from './KanbanBoard';

const PRIORITY_CONFIG = {
  low: { label: 'Baixa', bg: 'bg-gray-100 dark:bg-gray-500/10', text: 'text-gray-600 dark:text-gray-400' },
  medium: { label: 'Média', bg: 'bg-amber-100 dark:bg-amber-500/10', text: 'text-amber-700 dark:text-amber-400' },
  high: { label: 'Alta', bg: 'bg-orange-100 dark:bg-orange-500/10', text: 'text-orange-700 dark:text-orange-400' },
  urgent: { label: 'Urgente', bg: 'bg-red-100 dark:bg-red-500/10', text: 'text-red-700 dark:text-red-400' },
};

export default function Tarefas() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { usuariosList, listaSetoresTransferencia } = useSelector((state) => state.user);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('kanban');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // No workflow dialog
  const [noWorkflowDialogOpen, setNoWorkflowDialogOpen] = useState(false);
  const [creatingDefaultWorkflow, setCreatingDefaultWorkflow] = useState(false);

  // Workflows
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');

  // Filters
  const [filterResponsavel, setFilterResponsavel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPrioridade, setFilterPrioridade] = useState('all');
  const [filterSetor, setFilterSetor] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  useEffect(() => {
    if (user?.clienteId) {
      loadWorkflows();
      if (usuariosList.length === 0) dispatch(getListaUsuariosByClienteId(user.clienteId));
      if (listaSetoresTransferencia.length === 0) dispatch(getListaSetoresTransferencia(user.clienteId));
    }
  }, [user?.clienteId]);

  const loadWorkflows = async () => {
    try {
      const response = await tarefasService.listarWorkflows(user?.clienteId);
      const wfs = response.data || [];
      setWorkflows(wfs);
      if (wfs.length > 0 && !selectedWorkflowId) {
        setSelectedWorkflowId(wfs[0]._id);
      }
    } catch (error) {
      console.error('Erro ao carregar workflows:', error);
    }
  };

  useEffect(() => {
    if (user?.clienteId && selectedWorkflowId) {
      loadTasks();
    }
  }, [user?.clienteId, selectedWorkflowId]);

  const selectedWorkflow = useMemo(() =>
    workflows.find(w => w._id === selectedWorkflowId),
    [workflows, selectedWorkflowId]
  );

  const sortedEstagios = useMemo(() => {
    if (!selectedWorkflow?.estagios) return [];
    return [...selectedWorkflow.estagios].sort((a, b) => a.ordem - b.ordem);
  }, [selectedWorkflow]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedWorkflowId) params.workflow_id = selectedWorkflowId;
      const response = await tarefasService.listar(user?.clienteId, params);
      if (!response.error) {
        setTasks(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar tarefas:', error);
      toast({ title: "Erro", description: "Não foi possível carregar as tarefas", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Filtered tasks
  const filteredTasks = useMemo(() => {
    let result = tasks;
    if (searchTerm) result = result.filter(t => t.title?.toLowerCase().includes(searchTerm.toLowerCase()));
    if (filterResponsavel !== 'all') result = result.filter(t => (t.assigned_to?._id || t.assigned_to) === filterResponsavel);
    if (filterStatus !== 'all') result = result.filter(t => t.status === filterStatus);
    if (filterPrioridade !== 'all') result = result.filter(t => t.priority === filterPrioridade);
    if (filterSetor !== 'all') result = result.filter(t => (t.sector_id?._id || t.sector_id) === filterSetor);
    return result;
  }, [tasks, searchTerm, filterResponsavel, filterStatus, filterPrioridade, filterSetor]);

  // Pagination
  const totalPages = Math.ceil(filteredTasks.length / perPage);
  const paginatedTasks = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredTasks.slice(start, start + perPage);
  }, [filteredTasks, page, perPage]);

  useEffect(() => { setPage(1); }, [searchTerm, filterResponsavel, filterStatus, filterPrioridade, filterSetor]);

  // Active filters
  const activeFilters = useMemo(() => {
    const filters = [];
    if (filterResponsavel !== 'all') {
      const u = usuariosList.find(u => u._id === filterResponsavel);
      filters.push({ key: 'responsavel', label: u?.nomeFantasia || u?.nome || u?.email || 'Responsável', icon: 'mdi:account-outline', clear: () => setFilterResponsavel('all') });
    }
    if (filterStatus !== 'all') {
      filters.push({ key: 'status', label: filterStatus, icon: 'mdi:circle-outline', clear: () => setFilterStatus('all') });
    }
    if (filterPrioridade !== 'all') {
      const p = PRIORITY_CONFIG[filterPrioridade];
      filters.push({ key: 'prioridade', label: p?.label || filterPrioridade, icon: 'mdi:flag-outline', clear: () => setFilterPrioridade('all') });
    }
    if (filterSetor !== 'all') {
      const s = listaSetoresTransferencia.find(s => s._id === filterSetor);
      filters.push({ key: 'setor', label: s?.nome_setor || 'Setor', icon: 'mdi:account-group-outline', clear: () => setFilterSetor('all') });
    }
    return filters;
  }, [filterResponsavel, filterStatus, filterPrioridade, filterSetor, usuariosList, listaSetoresTransferencia]);

  const handleSave = async (data) => {
    if (editingTask) {
      const response = await tarefasService.editar(editingTask._id, data);
      if (!response.error) {
        setTasks(tasks.map(t => t._id === editingTask._id ? response.data : t));
        toast({ title: "Sucesso", description: "Tarefa atualizada" });
        return response.data;
      }
    } else {
      const response = await tarefasService.criar(user?.clienteId, data);
      if (!response.error) {
        setTasks([response.data, ...tasks]);
        toast({ title: "Sucesso", description: "Tarefa criada" });
        return response.data;
      }
    }
  };

  const handleDelete = async () => {
    if (!taskToDelete) return;
    try {
      const response = await tarefasService.deletar(taskToDelete._id);
      if (!response.error) {
        setTasks(tasks.filter(t => t._id !== taskToDelete._id));
        toast({ title: "Sucesso", description: "Tarefa deletada" });
      }
    } catch (error) {
      toast({ title: "Erro", description: "Não foi possível deletar a tarefa", variant: "destructive" });
    } finally {
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    const prev = tasks.find(t => t._id === taskId);
    setTasks(tasks.map(t => t._id === taskId ? { ...t, status: newStatus } : t));
    try {
      const response = await tarefasService.atualizarStatus(taskId, { status: newStatus, userId: user?.id });
      if (response.error) {
        setTasks(tasks.map(t => t._id === taskId ? prev : t));
      }
    } catch {
      setTasks(tasks.map(t => t._id === taskId ? prev : t));
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setDialogOpen(true);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const isOverdue = (task) => {
    if (!task.due_date) return false;
    const dueDate = new Date(task.due_date);
    const now = new Date();
    const isDone = isTaskDone(task);
    return dueDate < now && !isDone;
  };

  const isTaskDone = (task) => {
    if (selectedWorkflow?.estagios) {
      const sorted = [...selectedWorkflow.estagios].sort((a, b) => a.ordem - b.ordem);
      const lastStage = sorted[sorted.length - 1];
      return lastStage && task.status === lastStage.nome;
    }
    return task.status === 'done' || task.status === 'Concluído';
  };

  const renderStatusBadge = (task) => {
    const overdue = isOverdue(task);
    if (overdue) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-md bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400">
          Atrasada
        </span>
      );
    }
    const estagio = sortedEstagios.find(e => e.nome === task.status);
    if (estagio) {
      return (
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-md"
          style={{ backgroundColor: `${estagio.cor}18`, color: estagio.cor }}
        >
          {estagio.nome}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-md bg-gray-100 text-gray-600 dark:bg-gray-500/15 dark:text-gray-400">
        {task.status}
      </span>
    );
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === paginatedTasks.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paginatedTasks.map(t => t._id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const clearAllFilters = () => {
    setFilterResponsavel('all');
    setFilterStatus('all');
    setFilterPrioridade('all');
    setFilterSetor('all');
    setSearchTerm('');
  };

  const handleNovaTarefa = () => {
    if (workflows.length === 0) {
      setNoWorkflowDialogOpen(true);
      return;
    }
    setEditingTask(null);
    setDialogOpen(true);
  };

  return (
    <div className="h-full overflow-y-auto">

      <PageContainerHeader
        titleSection={
          <div className="flex items-center gap-2 min-w-fit">
            <div className="p-2 bg-[#7C3AED]/10 rounded-lg">
              <Iconify icon="mdi:checkbox-marked-circle-outline" className="text-[#7C3AED]" width={20} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-[#D1D2D3]">Tarefas</h3>
            </div>
          </div>
        }
        middleSection={
          <div className="flex flex-wrap items-end gap-2 flex-1">
            {workflows.length > 0 && (
              <div className="w-full sm:w-[180px]">
                <span className="text-[10px] font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider mb-1 block pl-1">Workflow</span>
                <Select value={selectedWorkflowId} onValueChange={v => setSelectedWorkflowId(v)}>
                  <SelectTrigger className="h-9 text-xs border-gray-200 dark:border-[#383838] bg-white dark:bg-[#222529] text-gray-900 dark:text-[#D1D2D3] focus:ring-[#7C3AED]/20">
                    <SelectValue placeholder="Workflow" />
                  </SelectTrigger>
                  <SelectContent>
                    {workflows.map(w => (
                      <SelectItem key={w._id} value={w._id}>{w.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {['admin', 'root', 'supervisor'].includes(user?.level) && (
            <div className="w-full sm:w-[180px]">
              <span className="text-[10px] font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider mb-1 block pl-1">Responsável</span>
              <Select value={filterResponsavel} onValueChange={v => setFilterResponsavel(v)}>
                <SelectTrigger className="h-9 text-xs border-gray-200 dark:border-[#383838] bg-white dark:bg-[#222529] text-gray-900 dark:text-[#D1D2D3] focus:ring-[#7C3AED]/20">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {usuariosList.map(u => (
                    <SelectItem key={u._id} value={u._id}>{u.nomeFantasia || u.nome || u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}

            <div className="w-full sm:w-[160px]">
              <span className="text-[10px] font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider mb-1 block pl-1">Status</span>
              <Select value={filterStatus} onValueChange={v => setFilterStatus(v)}>
                <SelectTrigger className="h-9 text-xs border-gray-200 dark:border-[#383838] bg-white dark:bg-[#222529] text-gray-900 dark:text-[#D1D2D3] focus:ring-[#7C3AED]/20">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {sortedEstagios.map(e => (
                    <SelectItem key={e.nome} value={e.nome}>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.cor }} />
                        {e.nome}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-full sm:w-[140px]">
              <span className="text-[10px] font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider mb-1 block pl-1">Prioridade</span>
              <Select value={filterPrioridade} onValueChange={v => setFilterPrioridade(v)}>
                <SelectTrigger className="h-9 text-xs border-gray-200 dark:border-[#383838] bg-white dark:bg-[#222529] text-gray-900 dark:text-[#D1D2D3] focus:ring-[#7C3AED]/20">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="low">Baixa</SelectItem>
                  <SelectItem value="medium">Média</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="urgent">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {activeFilters.length > 0 && (
              <div className="flex items-end">
                <button
                  onClick={clearAllFilters}
                  className="h-9 px-3 rounded-lg border border-dashed border-gray-300 dark:border-[#383838] text-xs text-gray-600 dark:text-[#ABABAD] font-medium flex items-center gap-2 hover:border-[#7C3AED] hover:text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors bg-transparent"
                >
                  <Iconify icon="mdi:filter-variant-remove" width={14} />
                  Limpar filtros
                </button>
              </div>
            )}
          </div>
        }
        rightSection={
          <div className="flex items-center gap-2 w-full md:w-auto justify-end">
            <div className="relative w-full sm:w-[200px]">
              <Input
                className="w-full pl-3 pr-10 h-9 bg-gray-50 dark:bg-[#1A1D21] border-gray-200 dark:border-[#383838] focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 hover:border-[#7C3AED]/50 transition-all text-sm rounded-lg dark:text-[#D1D2D3] dark:placeholder:text-[#ABABAD]"
                placeholder="Buscar tarefas..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
                <span className="flex items-center text-gray-400 dark:text-[#ABABAD]">
                  <Iconify icon="eva:search-outline" width={18} />
                </span>
              </div>
            </div>

            <div className="flex items-center bg-gray-50 dark:bg-[#1A1D21] rounded-lg border border-gray-200 dark:border-[#383838] p-0.5">
              <button
                onClick={() => setViewMode('list')}
                className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${viewMode === 'list' ? 'bg-[#7C3AED]/10 text-[#7C3AED]' : 'text-gray-500 dark:text-[#ABABAD] hover:text-gray-700 dark:hover:text-[#D1D2D3]'}`}
              >
                <Iconify icon="mdi:format-list-bulleted" width={16} />
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${viewMode === 'kanban' ? 'bg-[#7C3AED]/10 text-[#7C3AED]' : 'text-gray-500 dark:text-[#ABABAD] hover:text-gray-700 dark:hover:text-[#D1D2D3]'}`}
              >
                <Iconify icon="mdi:view-column-outline" width={16} />
              </button>
            </div>

            <Button
              className="h-9 bg-[#7C3AED] hover:bg-[#6D28D9] text-white shadow-sm shadow-[#7C3AED]/20 transition-all font-medium rounded-lg px-4 text-xs"
              onClick={handleNovaTarefa}
            >
              <Iconify icon="eva:plus-fill" className="mr-2" width={16} />
              Nova Tarefa
            </Button>
          </div>
        }
      />

        {/* Content */}
        <div className="max-w-[98%] mx-auto">
          {loading ? (
            <Card className="rounded-2xl shadow-sm border border-gray-100 dark:border-[#383838] dark:bg-[#222529] overflow-hidden">
              <div className="flex flex-col items-center justify-center h-64 text-gray-400 dark:text-[#ABABAD]">
                <Iconify icon="mdi:loading" className="w-8 h-8 animate-spin mb-4 text-[#7C3AED]" />
                <p className="text-sm font-medium">Carregando tarefas...</p>
              </div>
            </Card>
        ) : viewMode === 'kanban' ? (
          <KanbanBoard
            tasks={filteredTasks}
            onStatusChange={handleStatusChange}
            onEdit={handleEdit}
            workflow={selectedWorkflow}
            usuariosList={usuariosList}
          />
          ) : filteredTasks.length === 0 ? (
            <Card className="rounded-2xl shadow-sm border border-gray-100 dark:border-[#383838] dark:bg-[#222529] overflow-hidden">
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <div className="w-16 h-16 bg-gray-50 dark:bg-[#1A1D21] rounded-full flex items-center justify-center mb-4 text-gray-400 dark:text-[#ABABAD]">
                  <Iconify icon="mdi:clipboard-text-outline" width={32} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-[#D1D2D3] mb-2">Nenhuma tarefa encontrada</h3>
                <p className="text-gray-500 dark:text-[#ABABAD] max-w-sm mb-6">
                  {activeFilters.length > 0 ? 'Nenhuma tarefa corresponde aos filtros aplicados.' : 'Você ainda não criou nenhuma tarefa.'}
                </p>
                {activeFilters.length > 0 ? (
                  <Button variant="link" className="text-[#7C3AED] font-medium" onClick={clearAllFilters}>
                    Limpar filtros
                  </Button>
                ) : (
                  <Button variant="link" className="text-[#7C3AED] font-medium" onClick={handleNovaTarefa}>
                    Criar tarefa agora
                  </Button>
                )}
              </div>
            </Card>
          ) : (
            <Card className="rounded-2xl shadow-sm border border-gray-100 dark:border-[#383838] dark:bg-[#222529] overflow-hidden">
              <div className="p-3">
                <Table>
                <TableHeader className="bg-gray-50/80 dark:bg-[#1A1D21]/50">
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedIds.length === paginatedTasks.length && paginatedTasks.length > 0}
                        onCheckedChange={toggleSelectAll}
                        className="data-[state=checked]:bg-[#7C3AED] data-[state=checked]:border-[#7C3AED]"
                      />
                    </TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">Tarefas</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">Status</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">Prazo</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">Responsável</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">Criado por</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">Prioridade</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">Workflow</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedTasks.map((task) => {
                    const priorityCfg = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
                    const isSelected = selectedIds.includes(task._id);
                    const workflowName = task.workflow_id?.nome || selectedWorkflow?.nome || '-';
                    return (
                      <TableRow
                        key={task._id}
                        className={`group hover:bg-gray-50/50 dark:hover:bg-[#383838]/20 transition-colors border-b border-gray-50 dark:border-[#383838]/50 ${isSelected ? 'bg-[#7C3AED]/5' : ''}`}
                      >
                        <TableCell className="w-12" onClick={e => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(task._id)}
                            className="data-[state=checked]:bg-[#7C3AED] data-[state=checked]:border-[#7C3AED]"
                          />
                        </TableCell>
                        <TableCell className="cursor-pointer" onClick={() => handleEdit(task)}>
                          <div className="flex items-center gap-2.5">
                            <Iconify icon="mdi:clipboard-check-outline" width={16} className="text-[#7C3AED] flex-shrink-0" />
                            <span className="text-sm font-medium text-[#7C3AED] hover:underline">
                              {task.title || 'Sem título'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {renderStatusBadge(task)}
                        </TableCell>
                        <TableCell>
                          {task.due_date ? (
                            <span className={`text-sm ${isOverdue(task) ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-600 dark:text-[#ABABAD]'}`}>
                              {formatDateTime(task.due_date)}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400 italic">Sem prazo</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const assignedId = task.assigned_to?._id || task.assigned_to;
                            const populatedObj = typeof task.assigned_to === 'object' ? task.assigned_to : null;
                            const listUser = usuariosList.find(u => u._id === assignedId);
                            const displayText = populatedObj?.nomeFantasia || listUser?.nomeFantasia || populatedObj?.nome || listUser?.nome || populatedObj?.email || listUser?.email;
                            if (displayText) {
                              return (
                                <span className="text-sm text-gray-600 dark:text-[#D1D2D3] truncate max-w-[150px] inline-block" title={displayText}>
                                  {displayText}
                                </span>
                              );
                            }
                            return <span className="text-sm text-gray-400 italic">-</span>;
                          })()}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const createdById = task.created_by?._id || task.created_by;
                            const populatedCreator = typeof task.created_by === 'object' ? task.created_by : null;
                            const listCreator = usuariosList.find(u => u._id === createdById);
                            const creatorName = populatedCreator?.nomeFantasia || listCreator?.nomeFantasia || populatedCreator?.nome || listCreator?.nome || populatedCreator?.email || listCreator?.email;
                            if (creatorName) {
                              return (
                                <span className="text-sm text-gray-500 dark:text-[#ABABAD] truncate max-w-[150px] inline-block" title={creatorName}>
                                  {creatorName}
                                </span>
                              );
                            }
                            return <span className="text-sm text-gray-400 italic">-</span>;
                          })()}
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full ${priorityCfg.bg} ${priorityCfg.text}`}>
                            {priorityCfg.label}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-600 dark:text-[#ABABAD]">{workflowName}</span>
                        </TableCell>
                        <TableCell className="w-10" onClick={e => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 rounded-lg text-gray-400 dark:text-[#ABABAD] hover:text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10 opacity-0 group-hover:opacity-100 transition-all"
                            onClick={() => handleEdit(task)}
                            title="Ver detalhes"
                          >
                            <Iconify icon="mdi:information-outline" width={18} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-[#383838]">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-[#ABABAD]">
                <span>Exibindo</span>
                <Select value={String(perPage)} onValueChange={v => { setPerPage(Number(v)); setPage(1); }}>
                  <SelectTrigger className="w-[65px] h-8 rounded-lg border-gray-200 dark:border-[#383838] dark:bg-[#1A1D21] text-sm dark:text-[#D1D2D3]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
                <span>de {filteredTasks.length} tarefas</span>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 text-sm text-gray-500 dark:text-[#ABABAD] hover:text-gray-900 dark:hover:text-[#D1D2D3] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Anterior
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).slice(
                  Math.max(0, page - 3),
                  Math.min(totalPages, page + 2)
                ).map(p => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${p === page ? 'bg-[#7C3AED]/10 text-[#7C3AED] font-bold' : 'text-gray-500 dark:text-[#ABABAD] hover:bg-gray-100 dark:hover:bg-[#383838]'}`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 text-sm text-gray-500 dark:text-[#ABABAD] hover:text-gray-900 dark:hover:text-[#D1D2D3] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Próxima
                </button>
              </div>
            </div>
          </Card>
        )}

        </div> {/* max-w-[95%] */}

      {/* Create/Edit Dialog */}
      <TarefaDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditingTask(null); }}
        onSave={handleSave}
        onDelete={(task) => { setDialogOpen(false); setEditingTask(null); setTaskToDelete(task); setDeleteDialogOpen(true); }}
        task={editingTask}
        workflows={workflows}
        selectedWorkflowId={selectedWorkflowId}
      />

      {/* No Workflow Dialog */}
      <Dialog open={noWorkflowDialogOpen} onOpenChange={(open) => !creatingDefaultWorkflow && setNoWorkflowDialogOpen(open)}>
        <DialogContent className="sm:max-w-md rounded-2xl border-0 shadow-2xl dark:bg-[#222529]">
          <DialogHeader className="pt-6 px-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#7C3AED]/10">
                <Iconify icon="mdi:alert-circle-outline" width={24} className="text-[#7C3AED]" />
              </div>
              <DialogTitle className="text-xl font-bold text-gray-900 dark:text-[#D1D2D3]">Workflow necessário</DialogTitle>
            </div>
            <DialogDescription className="text-gray-500 dark:text-[#ABABAD] pt-2">
              Antes de criar tarefas, é necessário ter um workflow configurado. Ao continuar, criaremos um workflow padrão automaticamente que você poderá personalizar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="p-6 gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setNoWorkflowDialogOpen(false)}
              disabled={creatingDefaultWorkflow}
              className="rounded-xl border-gray-200 dark:border-[#383838] dark:text-[#D1D2D3]"
            >
              Fechar
            </Button>
            <Button
              disabled={creatingDefaultWorkflow}
              onClick={async () => {
                try {
                  setCreatingDefaultWorkflow(true);
                  await actionCriarWorkflow({
                    cliente_id: user?.clienteId,
                    nome: 'Workflow Padrão',
                    descricao: 'Workflow criado automaticamente. Personalize os estágios conforme sua necessidade.',
                    estagios: [
                      { nome: 'A fazer', cor: '#6b7280', ordem: 0 },
                      { nome: 'Em andamento', cor: '#3b82f6', ordem: 1 },
                      { nome: 'Concluído', cor: '#22c55e', ordem: 2 },
                    ],
                  });
                  setNoWorkflowDialogOpen(false);
                  toast({
                    title: "Workflow padrão criado",
                    description: "Um workflow padrão foi criado automaticamente. Personalize os estágios da forma que preferir.",
                  });
                  navigate('/dashboard/configuracoes/tarefas-config');
                } catch (error) {
                  console.error('Erro ao criar workflow padrão:', error);
                  toast({ title: "Erro", description: "Não foi possível criar o workflow padrão", variant: "destructive" });
                } finally {
                  setCreatingDefaultWorkflow(false);
                }
              }}
              className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-xl shadow-lg shadow-[#7C3AED]/20"
            >
              {creatingDefaultWorkflow ? (
                <><Iconify icon="mdi:loading" className="animate-spin mr-2" width={16} /> Criando...</>
              ) : (
                'Criar e Configurar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl border-0 shadow-2xl dark:bg-[#222529]">
          <DialogHeader className="pt-6 px-6">
            <DialogTitle className="text-xl font-bold text-gray-900 dark:text-[#D1D2D3]">Confirmar exclusão</DialogTitle>
            <DialogDescription className="text-gray-500 dark:text-[#ABABAD] pt-2">
              Tem certeza que deseja excluir a tarefa <span className="font-semibold text-gray-900 dark:text-[#D1D2D3]">"{taskToDelete?.title}"</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="p-6 gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="rounded-xl border-gray-200 dark:border-[#383838] dark:text-[#D1D2D3]"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl shadow-lg shadow-red-600/20"
            >
              Excluir Tarefa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
